import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CreditCard, CalendarClock, Banknote, Landmark, LifeBuoy, Lock } from "lucide-react";
import StripeCheckoutCard from "@/components/StripeCheckoutCard";
import { useAuth } from "@/context/AuthContext";

const STATUS_CLS = {
  "Free Trial": "bg-sky-500/15 text-sky-600 border-sky-500/30", "Active": "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  "Grace Period": "bg-yellow-500/15 text-yellow-600 border-yellow-500/30", "Payment Due": "bg-amber-500/15 text-amber-600 border-amber-500/30",
  "Past Due": "bg-orange-500/15 text-orange-600 border-orange-500/30", "Suspended": "bg-rose-500/15 text-rose-600 border-rose-500/30",
  "Restricted": "bg-rose-500/15 text-rose-600 border-rose-500/30",
};
const d = (s) => (s ? String(s).slice(0, 10) : "-");
const cur = (n, c) => `${c} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

// Owner view — reads the SAME /billing/me summary + platform payment methods Super Admin manages.
export default function Billing() {
  const [b, setB] = useState(null);
  const [stripeOn, setStripeOn] = useState(null); // null = loading
  const { loadBusiness, business } = useAuth();
  useEffect(() => {
    api.get("/billing/me").then((r) => setB(r.data));
    api.get("/billing/stripe/me").then((r) => setStripeOn(!!r.data?.billing_enabled)).catch(() => setStripeOn(false));
    loadBusiness();
  }, []); // eslint-disable-line
  if (!b || stripeOn === null) return <p className="text-muted-foreground">Loading…</p>;
  const trialOver = b.trial_ends_at && b.status !== "Free Trial";
  const showDeadline = b.restriction_date && !["Active", "Free Trial"].includes(b.status);
  if (stripeOn) {
    // Stripe-billed NEEC POS tenant: the Stripe card is the single source of truth; legacy/manual billing content is not applicable.
    return (
      <div className="space-y-6 max-w-3xl" data-testid="billing-page">
        <div>
          <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><CreditCard className="w-7 h-7 text-emerald-500" /> Subscription &amp; Billing</h1>
          <p className="text-muted-foreground">Your NEEC POS subscription, billed securely by card. Only you (the Owner) can see this.</p>
        </div>
        {b.status === "Restricted" && (
          <Card className="p-4 border-amber-500/40 bg-amber-500/5" data-testid="billing-restricted-notice">
            <p className="font-semibold text-amber-700 flex items-center gap-2"><Lock className="w-4 h-4" /> {business?.billing_restriction_kind === "overdue" ? "Account access is temporarily limited" : "Payment required"}</p>
            <p className="text-sm text-muted-foreground mt-1">{business?.billing_restriction_kind === "overdue" ? "Your subscription payment is overdue and the grace period has ended. Your business data is retained and unchanged. Update your payment method with Manage Billing below — access is restored automatically as soon as the payment succeeds." : "Your free access has ended. Complete your payment below to continue using NEEC POS. Your business data is retained and unchanged."}</p>
          </Card>
        )}
        <StripeCheckoutCard />
        {(b.support?.email || b.support?.phone) && <p className="text-xs text-muted-foreground flex items-center gap-2"><LifeBuoy className="w-3.5 h-3.5" /> Support: {[b.support.email, b.support.phone].filter(Boolean).join(" · ")}</p>}
      </div>
    );
  }
  return (
    <div className="space-y-6 max-w-3xl" data-testid="billing-page">
      <div>
        <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><CreditCard className="w-7 h-7 text-emerald-500" /> Subscription &amp; Billing</h1>
        <p className="text-muted-foreground">Your NEEC POS subscription status and payment instructions. Only you (the Owner) can see this.</p>
      </div>

      {b.status === "Restricted" && (
        <Card className="p-4 border-rose-500/40 bg-rose-500/5" data-testid="billing-restricted-notice">
          <p className="font-semibold text-rose-600 flex items-center gap-2"><Lock className="w-4 h-4" /> Your account is restricted for non-payment</p>
          <p className="text-sm text-muted-foreground mt-1">{b.restriction_reason || "Payment was not received by the deadline."} New sales, invoices and estimates are paused. Pay the amount due below and access is restored automatically once NEEC confirms the payment.</p>
        </Card>
      )}

      <StripeCheckoutCard />

      <Card className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Account Status</p>
            <Badge className={`mt-1 text-sm ${STATUS_CLS[b.status] || ""}`} data-testid="billing-status">{b.status}</Badge></div>
          <div className="text-right"><p className="text-xs uppercase tracking-wider text-muted-foreground">Amount Due</p>
            <p className="mono text-2xl font-extrabold" data-testid="billing-amount-due">{cur(b.amount_due, b.currency)}</p></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
          <div>Monthly subscription: <b data-testid="billing-monthly">{b.monthly_rate != null ? cur(b.monthly_rate, b.currency) : "To be confirmed"}</b></div>
          <div>Currency: <b data-testid="billing-currency">{b.currency}</b></div>
          {b.setup_fee != null && <div>One-time setup fee: <b data-testid="billing-setup-fee">{b.setup_fee_paid ? "Paid" : cur(b.setup_fee, b.currency)}</b></div>}
          {b.trial_ends_at && <div className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-muted-foreground" /> Trial end: <b data-testid="billing-trial-end">{d(b.trial_ends_at)}</b>{trialOver && <span className="text-xs text-muted-foreground">(ended)</span>}</div>}
          <div className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-muted-foreground" /> Next payment due: <b data-testid="billing-next-due">{d(b.next_due)}</b></div>
          {b.paid_through && <div>Paid through: <b>{d(b.paid_through)}</b></div>}
          {showDeadline && <div className="flex items-center gap-2 text-rose-600"><CalendarClock className="w-4 h-4" /> Restriction date: <b data-testid="billing-restriction-date">{b.restriction_date}</b></div>}
        </div>
      </Card>

      {!b.pricing_assigned && <Card className="p-4 border-amber-500/30 bg-amber-500/5" data-testid="billing-pending"><p className="text-sm">Your account pricing will be confirmed by NEEC DIGITAL.</p></Card>}

      <Card className="p-6" data-testid="billing-instructions">
        <p className="font-head font-bold mb-3">How to Pay</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {(b.payment_methods || []).map((m) => (
            <div key={m.id} className="rounded-xl border border-border p-4" data-testid={`billing-method-${m.id}`}>
              <p className="font-semibold flex items-center gap-2 mb-1"><Landmark className="w-4 h-4 text-emerald-500" /> {m.name}</p>
              {m.instructions && <p className="text-xs text-muted-foreground mb-2">{m.instructions}</p>}
              <p className="text-sm whitespace-pre-line mono">{m.details}</p>
              {m.reference_instructions && <p className="text-xs text-muted-foreground mt-2 italic">{m.reference_instructions}</p>}
              {m.note && <p className="text-xs text-muted-foreground mt-1">{m.note}</p>}
            </div>
          ))}
          {!(b.payment_methods || []).length && <p className="text-sm text-muted-foreground">Payment details will be provided by NEEC.</p>}
        </div>
        <p className="text-xs text-muted-foreground mt-3 flex items-center gap-2"><Banknote className="w-3.5 h-3.5" /> After paying, NEEC confirms your payment and your status updates automatically.</p>
        {(b.support?.email || b.support?.phone) && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2"><LifeBuoy className="w-3.5 h-3.5" /> Support: {[b.support.email, b.support.phone].filter(Boolean).join(" · ")}</p>}
      </Card>

      <Card className="p-6" data-testid="billing-history">
        <p className="font-head font-bold mb-3">Recent Payments</p>
        {!(b.payments || []).length && <p className="text-sm text-muted-foreground">No payments recorded yet.</p>}
        {(b.payments || []).map((p) => (
          <div key={p.id} className="flex items-center justify-between text-sm border-b border-border/50 py-1.5">
            <span>{d(p.paid_on)}</span><span className="mono font-semibold">{cur(p.amount, p.currency)}</span>
            <span className="text-muted-foreground">{p.method}{p.is_setup_fee ? " · setup fee" : p.period_end ? ` · through ${d(p.period_end)}` : ""}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}
